import { atomFamily, DefaultValue, selectorFamily, useRecoilState, useRecoilValue } from 'recoil';

export type ViewedChange = {
    /**
     * ID of the page document at the moment it was last marked as viewed.
     */
    documentId: string | null;
};

/**
 * Atom storing the changes that have been viewed for a change request.
 */
const viewedChangesAtomFamily = atomFamily<Map<string, ViewedChange>, string>({
    key: 'viewedChangesAtomFamily',
    default: new Map(),
});

/**
 * Selector for a specific page change in a change request.
 */
const viewedPageChangeSelector = selectorFamily<
    false | ViewedChange,
    { changeRequestId: string; pageId: string }
>({
    key: 'viewedPageChangeSelector',
    get:
        ({ changeRequestId, pageId }) =>
        ({ get }) => {
            return get(viewedChangesAtomFamily(changeRequestId)).get(pageId) ?? false;
        },
    set:
        ({ changeRequestId, pageId }) =>
        ({ set }, newValue) => {
            set(viewedChangesAtomFamily(changeRequestId), (prev) => {
                const newMap = new Map(prev);
                if (newValue instanceof DefaultValue || newValue === false) {
                    newMap.delete(pageId);
                } else {
                    newMap.set(pageId, newValue);
                }
                return newMap;
            });
        },
});

/**
 * Hook to get the count of changes viewed in a change request.
 */
export function useViewedChanges(input: { changeRequestId: string }) {
    return useRecoilValue(viewedChangesAtomFamily(input.changeRequestId));
}

/**
 * Hook to get and set the viewed state of a page change in a change request.
 */
export function useViewedPageChange(input: { changeRequestId: string; pageId: string }) {
    return useRecoilState(viewedPageChangeSelector(input));
}
